import { useState, useEffect, useMemo, useRef } from 'react'
import { IntercomUser } from 'tools/intercom'
import { isCarrierLogin, isAgentLogin, isInsuranceLogin } from 'tools/auth'

export const useAuth = (): { user: IntercomUser | null; authReady: boolean } => {
  const [userData, setUserData] = useState<{
    userId: string | null
    userName: string | null
    email: string | undefined
    userType: string | null
    companyId: string | null
    companyName: string | null
    companyTypeId: string | null
    isInsurance: boolean
    userRole: string
    intercomBucket: 'user' | 'lead'
  } | null>(null)
  const [authReady, setAuthReady] = useState<boolean>(false)
  const createdAtIndexRef = useRef<number | null>(null)
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const authReadySetRef = useRef<boolean>(false)

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken')
      const userId = localStorage.getItem('userId')
      const userName = localStorage.getItem('userName')
      const userLogin = localStorage.getItem('userLogin')
      const companyProfileStr = localStorage.getItem('companyProfile')
      const userType = localStorage.getItem('userType')
      const companyTypeId = localStorage.getItem('companyTypeId')
      const companyId = localStorage.getItem('companyId')
      const companyName = localStorage.getItem('companyName')
      const isInsurance = localStorage.getItem('isInsurance')

      if (!token || !userId) {
        setUserData(prevData => {
          if (prevData === null) {
            return prevData
          }
          return null
        })
        if (!authReadySetRef.current) {
          setAuthReady(true)
          authReadySetRef.current = true
        }
        createdAtIndexRef.current = null
        return
      }

      // userLogin is the account's actual login email -- confirmed via real UAT accounts to be
      // reliably correct for carrier/agent/insurance logins alike, unlike userName/companyProfile
      // below, which can hold a company name (carrier) or a bare label like "UI" (some insurance
      // accounts) instead of an email. Kept as a fallback chain, not a replacement, since
      // userLogin hasn't been verified present for every possible account type.
      let email = userName || undefined
      if (companyProfileStr) {
        try {
          const companyProfile = JSON.parse(companyProfileStr)
          email = companyProfile?.email || email
        } catch (e) {
        }
      }
      email = userLogin || email

      // Named userRole, not role -- Intercom reserves the top-level `role` field for its own
      // User/Lead classification and rejects a custom attribute with that same name.
      let userRole = 'user'
      if (isCarrierLogin()) {
        userRole = 'carrier'
      } else if (isAgentLogin()) {
        userRole = 'agent'
      } else if (isInsuranceLogin()) {
        userRole = 'insurance'
      }

      // isInsuranceLogin() covers both Is_Insurance and companyTypeId 7 (TECH_PARTNER) —
      // this already matches the "paying customer = insurance or tech partner" rule.
      const intercomBucket: 'user' | 'lead' = isInsuranceLogin() ? 'user' : 'lead'

      if (createdAtIndexRef.current === null) {
        createdAtIndexRef.current = Math.floor(Date.now() / 1000)
      }

      setUserData(prevData => {
        if (
          prevData?.userId === userId &&
          prevData?.userName === userName &&
          prevData?.email === email &&
          prevData?.userType === userType &&
          prevData?.companyId === companyId &&
          prevData?.companyName === companyName &&
          prevData?.companyTypeId === companyTypeId &&
          prevData?.isInsurance === (isInsurance === 'true') &&
          prevData?.userRole === userRole &&
          prevData?.intercomBucket === intercomBucket
        ) {
          return prevData
        }

        return {
          userId,
          userName,
          email,
          userType,
          companyId,
          companyName,
          companyTypeId,
          isInsurance: isInsurance === 'true',
          userRole,
          intercomBucket
        }
      })

      if (!authReadySetRef.current) {
        setAuthReady(true)
        authReadySetRef.current = true
      }
    }

    checkAuth()

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' || e.key === 'userId' || e.key === 'userName' || e.key === 'userLogin' || e.key === 'companyProfile') {
        checkAuth()
      }
    }

    const handleAuthStateChange = () => {
      checkAuth()
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (visibilityTimeoutRef.current) {
          clearTimeout(visibilityTimeoutRef.current)
        }
        visibilityTimeoutRef.current = setTimeout(() => {
          checkAuth()
        }, 500)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('authStateChange', handleAuthStateChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('authStateChange', handleAuthStateChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current)
      }
    }
  }, [])

  const user = useMemo<IntercomUser | null>(() => {
    if (!userData || !userData.userId) {
      return null
    }

    return {
      user_id: userData.userId,
      email: userData.email,
      name: userData.userName || undefined,
      created_at: createdAtIndexRef.current || Math.floor(Date.now() / 1000),
      user_role: userData.userRole,
      user_type: userData.userType || undefined,
      company_id: userData.companyId || undefined,
      company_name: userData.companyName || undefined,
      company_type_id: userData.companyTypeId || undefined,
      is_insurance: userData.isInsurance,
      is_authenticated: true,
      intercom_bucket: userData.intercomBucket
    }
  }, [userData])

  return { user, authReady }
}
